/**
 * Viral song promos (Oct 29 2026, from Zeely's "Viral video"): short vertical
 * clips in the formats musicians post on TikTok, Reels and Shorts (vibing in
 * the car, hearing it for the first time, a night walk with headphones…),
 * recreated with the user's artist over a section of their song, with a
 * styled hook headline on top.
 *
 * Each format's reference clip is one WE generated (scripts/viral-promo-formats/
 * render.mts, fictional people), never a scraped video. The artist (the
 * user's own photo or a saved character, with the consent line ticked)
 * replaces the person in the clip: Wan 2.2 Animate in replace mode, which
 * keeps the clip's scene and motion (a face swap only changed the face and
 * kept the clip's hair and clothes, Oct 29 probe). Then our own ffmpeg puts
 * the song section under it and burns the headline (server/viralPromoJob.ts).
 *
 * Lip-sync (Nov 1 2026): on the formats where the artist would sing
 * (VIRAL_PROMO_LIPSYNC_FORMAT_IDS) the user can have the artist sing the
 * part instead. Then nothing is replaced: the artist is drawn into the
 * format's scene as a start frame, and LTX 2.3 animates it from the part's
 * isolated vocals (server/viralPromoLipsync.ts), so the face, hair and
 * clothes are the artist's own and the mouth follows the song. A lip-sync
 * promo can run up to VIRAL_PROMO_LIPSYNC_MAX_SECONDS; a replaced one is
 * bounded by its format's clip.
 *
 * Priced like the dance tool, the same motion engine: per second of clip at
 * its resolution (shared/danceVideo.ts DANCE_VIDEO_RATES), 3-second floor,
 * the same rate with or without lip-sync. The user picks the length, 5
 * seconds up to the engine's ceiling (Oct 31 2026; 15 s with lip-sync since
 * Nov 1). Kill switches: CREATIVE_VIRAL_PROMO=off, and
 * CREATIVE_VIRAL_PROMO_LIPSYNC=off for lip-sync alone.
 */
import type { CreativeTool } from "./creativeTools";
import { DANCE_VIDEO_MIN_BILLABLE_SECONDS, DANCE_VIDEO_RATES } from "./danceVideo";
import media from "./viralPromoMedia.json";

export const VIRAL_PROMO_TOOL_ID = "viral-promo";
/**
 * The page. Indexed since Nov 2 2026 as the song promo video maker, with the
 * creative pages' copy under the catalog card's id (VIRAL_PROMO_CARD_ID).
 */
export const VIRAL_PROMO_PAGE_PATH = "/viral-song-promo";
/**
 * The catalog card that links the page (shared/creativeTools.ts), whose id is
 * also the page's creative SEO key (shared/creativeToolsSeoLocales).
 */
export const VIRAL_PROMO_CARD_ID = "viral-song-promo";
/** The engine a promo records (creative_tool_jobs.model). */
export const VIRAL_PROMO_MODEL = "wan-2.2-animate-replace";
/** The engine a lip-synced promo records. */
export const VIRAL_PROMO_LIPSYNC_MODEL = "ltx-2.3-lipsync";

export const VIRAL_PROMO_RESOLUTIONS = ["480p", "720p"] as const;
export type ViralPromoResolution = (typeof VIRAL_PROMO_RESOLUTIONS)[number];
/** Credits per second of clip: the dance tool's rate on the same engine. */
export const VIRAL_PROMO_RATES: Record<ViralPromoResolution, number> = DANCE_VIDEO_RATES;
export const viralPromoBilledSeconds = (seconds: number) =>
  Math.max(DANCE_VIDEO_MIN_BILLABLE_SECONDS, Math.ceil(seconds));
export const viralPromoCredits = (seconds: number, resolution: ViralPromoResolution) =>
  viralPromoBilledSeconds(seconds) * VIRAL_PROMO_RATES[resolution];

/**
 * The formats, in the order the gallery shows them. `seconds` is the clip's
 * length, the longest promo it makes (Oct 31 2026: 10 s, was 5); `vibe` is
 * the format in a few English words for the headline writer's brief. Media
 * URLs live in viralPromoMedia.json.
 */
export const VIRAL_PROMO_FORMAT_DEFINITIONS = [
  { id: "car-vibe", seconds: 10, vibe: "vibing to the song in the car" },
  { id: "first-listen", seconds: 10, vibe: "hearing the song for the first time and loving it" },
  { id: "night-walk", seconds: 10, vibe: "walking alone at night with headphones on" },
  { id: "studio-session", seconds: 10, vibe: "recording the song in the studio" },
  { id: "on-stage", seconds: 10, vibe: "performing the song on stage" },
  { id: "mirror-check", seconds: 10, vibe: "an outfit check in the mirror" },
  { id: "bedroom-dance", seconds: 10, vibe: "dancing to the song in their room" },
  { id: "rooftop-sunset", seconds: 10, vibe: "on a rooftop at sunset" },
] as const;
export type ViralPromoFormatId = (typeof VIRAL_PROMO_FORMAT_DEFINITIONS)[number]["id"];
type FormatMedia = {
  video: string;
  poster: string;
  hover: string;
  /** The clip cut to each shorter whole length, by its seconds ("5" to "9"). */
  cuts?: Record<string, string>;
};
export type ViralPromoFormat = (typeof VIRAL_PROMO_FORMAT_DEFINITIONS)[number] & FormatMedia;
const formatMedia = media.formats as Record<string, FormatMedia>;
/** The library: only formats whose clip exists. */
export const viralPromoFormats: ViralPromoFormat[] = VIRAL_PROMO_FORMAT_DEFINITIONS.flatMap((format) =>
  formatMedia[format.id] ? [{ ...format, ...formatMedia[format.id] }] : [],
);
export const findViralPromoFormat = (id: string | undefined) =>
  viralPromoFormats.find((format) => format.id === id);

/**
 * The formats where the artist can sing the part (lip-sync). Not the ones
 * where singing would read wrong: hearing the song for the first time is a
 * reaction, and the mirror check holds a phone in front of the face.
 */
export const VIRAL_PROMO_LIPSYNC_FORMAT_IDS: readonly ViralPromoFormatId[] = [
  "car-vibe",
  "studio-session",
  "on-stage",
  "night-walk",
  "bedroom-dance",
  "rooftop-sunset",
];
export const viralPromoLipSyncFormat = (format: Pick<ViralPromoFormat, "id"> | undefined) =>
  Boolean(format && (VIRAL_PROMO_LIPSYNC_FORMAT_IDS as readonly string[]).includes(format.id));

/**
 * How long a promo is: the user picks the part of the song, in whole
 * seconds, from VIRAL_PROMO_MIN_SECONDS up to the format's clip, or up to
 * VIRAL_PROMO_LIPSYNC_MAX_SECONDS when the artist sings it (the clip is
 * made for the part, so no format clip bounds it). A new promo starts at
 * VIRAL_PROMO_DEFAULT_SECONDS.
 */
export const VIRAL_PROMO_MIN_SECONDS = 5;
export const VIRAL_PROMO_DEFAULT_SECONDS = 10;
export const VIRAL_PROMO_LIPSYNC_MAX_SECONDS = 15;
export const VIRAL_PROMO_MAX_SECONDS = Math.max(
  VIRAL_PROMO_LIPSYNC_MAX_SECONDS,
  ...VIRAL_PROMO_FORMAT_DEFINITIONS.map((format) => format.seconds),
);
/** The longest promo a format makes, with or without lip-sync. */
export const viralPromoMaxSeconds = (format: ViralPromoFormat, lipSync = false) =>
  lipSync && viralPromoLipSyncFormat(format) ? VIRAL_PROMO_LIPSYNC_MAX_SECONDS : format.seconds;
export const viralPromoSeconds = (format: ViralPromoFormat, seconds: number, lipSync = false) =>
  Math.min(viralPromoMaxSeconds(format, lipSync), Math.max(VIRAL_PROMO_MIN_SECONDS, Math.round(seconds)));
/** The clip the engine follows for a promo that long, so it renders (and costs) only those seconds. */
export const viralPromoClipUrl = (format: ViralPromoFormat, seconds: number) =>
  (seconds < format.seconds && format.cuts?.[String(seconds)]) || format.video;

/**
 * The hook headline's fonts (bundled in server/fonts, so the burn matches).
 * `emPerSize` is the font's em as a share of its ASS font size: libass sizes
 * a font so its Windows ascent plus descent fills the size (unitsPerEm /
 * (usWinAscent + usWinDescent) of `file`; the tests read the files), so one
 * ASS size draws a different text size in each font.
 */
export const VIRAL_HEADLINE_FONTS = [
  { id: "bold", family: "Montserrat Black", file: "Montserrat-Black.ttf", emPerSize: 0.64 },
  { id: "impact", family: "Anton", file: "Anton-Regular.ttf", emPerSize: 0.577 },
  { id: "tall", family: "Bebas Neue", file: "BebasNeue-Regular.ttf", emPerSize: 0.769 },
  { id: "heavy", family: "Archivo Black", file: "ArchivoBlack-Regular.ttf", emPerSize: 0.742 },
  { id: "serif", family: "Lora", file: "Lora-Bold.ttf", emPerSize: 0.667 },
  { id: "playful", family: "Comic Neue", file: "ComicNeue-Bold.ttf", emPerSize: 0.741 },
] as const;
export type ViralHeadlineFont = (typeof VIRAL_HEADLINE_FONTS)[number]["id"];
/** Outline: text with a thick outline. Box: text on a solid box. Shadow: text with a soft shadow. */
export const VIRAL_HEADLINE_LOOKS = ["outline", "box", "shadow"] as const;
export type ViralHeadlineLook = (typeof VIRAL_HEADLINE_LOOKS)[number];
/** Inside the safe area: under the app's top bar, the middle, or above its caption zone. */
export const VIRAL_HEADLINE_POSITIONS = ["top", "center", "bottom"] as const;
export type ViralHeadlinePosition = (typeof VIRAL_HEADLINE_POSITIONS)[number];
export const VIRAL_HEADLINE_SIZES = ["s", "m", "l"] as const;
export type ViralHeadlineSize = (typeof VIRAL_HEADLINE_SIZES)[number];
/** Each size's em as a share of the frame's short side, the same in every font. */
export const VIRAL_HEADLINE_EM: Record<ViralHeadlineSize, number> = { s: 0.048, m: 0.06, l: 0.075 };
/**
 * The looks' strokes in ems, so the burn (server/viralPromoRender.ts) and the
 * page's preview draw them alike: the outline around the text and its drop,
 * the box's padding, and the shadow's offset.
 */
export const VIRAL_HEADLINE_STROKES = { outline: 0.13, outlineShadow: 0.06, boxPadding: 0.3, shadow: 0.1 } as const;
export const VIRAL_HEADLINE_MAX_CHARS = 80;

export type ViralHeadline = {
  text: string;
  font: ViralHeadlineFont;
  look: ViralHeadlineLook;
  /** "#rrggbb" */
  textColor: string;
  /** The outline's, box's or shadow's color, "#rrggbb". */
  accentColor: string;
  position: ViralHeadlinePosition;
  size: ViralHeadlineSize;
};
export const DEFAULT_VIRAL_HEADLINE: Omit<ViralHeadline, "text"> = {
  font: "bold",
  look: "outline",
  textColor: "#ffffff",
  accentColor: "#000000",
  position: "top",
  size: "m",
};

/**
 * The server's answer when a promo arrives without the consent line ticked.
 * The face is always the user's choice of a real or saved person, so the
 * line is always asked (the face swap's, translated on the page).
 */
export const VIRAL_PROMO_CONSENT_REQUIRED =
  "Confirm that the face is yours, or that you have the person's permission, before it is put in a video.";

/**
 * The job's tool entry, so a promo lists, plays, downloads and refunds like
 * any other creative result (findCreativeToolForJob). Not in creativeTools:
 * its page is its own (VIRAL_PROMO_PAGE_PATH; the catalog links to it), and
 * the generic create refuses it.
 */
export const viralPromoTool: CreativeTool = {
  id: VIRAL_PROMO_TOOL_ID,
  name: "Viral song promo",
  description: "A viral short-video format recreated with your artist over your song.",
  category: "video",
  // The artist's photo, then the format's clip.
  fields: ["image", "video"],
  models: [VIRAL_PROMO_MODEL],
};
